import { createSelectorHooks } from 'auto-zustand-selectors-hook';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { type User } from '$/services/usecases/authentication/mapper/genericCloudResponse';
import { type UserRole } from '$/services/usecases/authentication/mapper/jwt';

export type AuthModalType =
  | 'login'
  | 'register'
  | 'registerSchool'
  | 'registerOtp'
  | 'passwordForgotten'
  | 'registerSuccess'
  | 'versionComparison'
  | 'loginToSave'
  | 'sapCxLinking'
  | 'sapCxLinkingSuccess'
  | 'sapCxConsentRequired'
  | 'sapCxMigration';

type State = {
  user: User | null;
  userRole: UserRole;
  modalType: AuthModalType;
  isModalOpen: boolean;
  pendingAuthEmail: string;
  pendingAuthToken: string;
  migrationPassword: string | null;
  accessToken: string;
  refreshToken: string;
  flutterCallbackChannel: string | null;
  refreshFailed: boolean;
  showRegisterSchoolInfo: boolean;
};

type Action = {
  authenticate: (user: User, token: string) => void;
  setUser: (user: User) => void;
  setModalType: (type: AuthModalType) => void;
  onModalOpen: (type?: AuthModalType) => void;
  onModalClose: () => void;
  setPendingAuthEmail: (email: string) => void;
  setPendingAuthToken: (token: string) => void;
  setRefreshToken: (token: string) => void;
  clearRefreshToken: () => void;
  initSapCxMigration: (params: { password: string; email: string }) => void;
  initSapCxLinking: (params: { password: string; email: string }) => void;
};

const initial: State = {
  user: null,
  pendingAuthEmail: '',
  pendingAuthToken: '',
  migrationPassword: null,
  modalType: 'login',
  isModalOpen: false,
  accessToken: '',
  refreshToken: '',
  userRole: 'free',
  refreshFailed: false,
  showRegisterSchoolInfo: false,
  flutterCallbackChannel: null,
};

export const store = create<State & Action>()(
  persist(
    (set) => ({
      ...initial,
      authenticate: (user, token) => {
        set({
          user,
          accessToken: token,
          userRole: user.role,
          migrationPassword: null,
        });
      },
      setUser: (user) => set({ user, migrationPassword: null }),
      setModalType: (modalType) => set({ modalType, migrationPassword: null }),
      onModalClose: () =>
        set({
          isModalOpen: false,
          showRegisterSchoolInfo: false,
          migrationPassword: null,
        }),
      onModalOpen: (modalType) => {
        set({ isModalOpen: true });
        if (modalType != null) set({ modalType });
      },
      setPendingAuthEmail: (otpEmail) => set({ pendingAuthEmail: otpEmail }),
      setPendingAuthToken: (otpToken) => set({ pendingAuthToken: otpToken }),
      setRefreshToken: (refreshToken) => set({ refreshToken }),
      clearRefreshToken: () => set({ refreshToken: '' }),
      initSapCxMigration: ({ email, password }) =>
        set({
          migrationPassword: password,
          pendingAuthEmail: email,
          modalType: 'sapCxMigration',
        }),
      initSapCxLinking: ({ email, password }) =>
        set({
          migrationPassword: password,
          pendingAuthEmail: email,
          modalType: 'sapCxLinking',
        }),
    }),
    {
      name: 'authentication-store',
      partialize: (s) => ({ refreshFailed: s.refreshFailed }),
    },
  ),
);

export const useAuthenticationStore = createSelectorHooks(store);
